// src/contexts/ThemeContext.js
// Contexte de thème simplifié et adapté à l'architecture existante de Bobo Food Plan

import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

// Création du contexte
const ThemeContext = createContext();

// Hook personnalisé pour utiliser le contexte
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme doit être utilisé dans un ThemeProvider');
  }
  return context;
};

// Provider du contexte de thème
export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('light'); // 'light' ou 'dark'
  const [loading, setLoading] = useState(true);

  // ====================================
  // CHARGEMENT DU THÈME AU DÉMARRAGE
  // ====================================
  useEffect(() => {
    const loadTheme = async () => {
      try {
        // 1. Essayer de charger depuis localStorage en premier (plus rapide)
        const savedTheme = localStorage.getItem('bobo-food-plan-theme');
        if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
          setTheme(savedTheme);
          applyThemeToDocument(savedTheme);
        }

        // 2. Si utilisateur connecté, récupérer depuis Firestore
        if (auth.currentUser) {
          await loadUserThemePreference();
        }
      } catch (error) {
        console.error('Erreur lors du chargement du thème:', error);
        // En cas d'erreur, rester sur le thème par défaut
      } finally {
        setLoading(false);
      }
    };

    loadTheme();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ====================================
  // ÉCOUTER LES CHANGEMENTS D'UTILISATEUR
  // ====================================
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async user => {
      if (user) {
        // Utilisateur connecté : charger ses préférences
        await loadUserThemePreference();
      } else {
        // Utilisateur déconnecté : utiliser localStorage ou défaut
        const savedTheme = localStorage.getItem('bobo-food-plan-theme');
        if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
          setTheme(savedTheme);
          applyThemeToDocument(savedTheme);
        } else {
          setTheme('light');
          applyThemeToDocument('light');
        }
      }
    });

    return () => unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ====================================
  // FONCTIONS UTILITAIRES
  // ====================================

  // Charger les préférences utilisateur depuis Firestore
  const loadUserThemePreference = async () => {
    try {
      if (!auth.currentUser) return;

      const userRef = doc(db, 'Users', auth.currentUser.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const userData = userSnap.data();
        const userTheme = userData.themePreference || 'light';

        if (userTheme !== theme) {
          setTheme(userTheme);
          applyThemeToDocument(userTheme);
          localStorage.setItem('bobo-food-plan-theme', userTheme);
        }
      }
    } catch (error) {
      console.error(
        'Erreur lors du chargement de la préférence utilisateur:',
        error
      );
    }
  };

  // Sauvegarder les préférences utilisateur dans Firestore
  const saveUserThemePreference = async newTheme => {
    try {
      if (!auth.currentUser) return;

      const userRef = doc(db, 'Users', auth.currentUser.uid);
      await setDoc(
        userRef,
        {
          themePreference: newTheme,
          lastThemeUpdate: new Date(),
        },
        { merge: true }
      );

    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la préférence:', error);
    }
  };

  // Appliquer le thème au document
  const applyThemeToDocument = themeName => {
    // Supprimer les classes de thème existantes
    document.body.classList.remove('light', 'dark');

    // Ajouter la nouvelle classe de thème
    document.body.classList.add(themeName);

    // Mettre à jour l'attribut data-theme pour les sélecteurs CSS avancés
    document.documentElement.setAttribute('data-theme', themeName);

  };

  // ====================================
  // FONCTION DE BASCULEMENT DE THÈME
  // ====================================
  const toggleTheme = async () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';

    try {
      // 1. Mettre à jour l'état local
      setTheme(newTheme);

      // 2. Appliquer immédiatement au document
      applyThemeToDocument(newTheme);

      // 3. Sauvegarder en localStorage
      localStorage.setItem('bobo-food-plan-theme', newTheme);

      // 4. Sauvegarder en Firestore si utilisateur connecté
      if (auth.currentUser) {
        await saveUserThemePreference(newTheme);
      }

    } catch (error) {
      console.error('Erreur lors du basculement de thème:', error);
      // En cas d'erreur, revenir au thème précédent
      setTheme(theme);
      applyThemeToDocument(theme);
    }
  };

  // ====================================
  // VALEURS DU CONTEXTE
  // ====================================
  const contextValue = {
    // État du thème
    theme,
    isDark: theme === 'dark',
    isLight: theme === 'light',
    loading,

    // Actions
    toggleTheme,
    setTheme: newTheme => {
      if (newTheme === 'light' || newTheme === 'dark') {
        setTheme(newTheme);
        applyThemeToDocument(newTheme);
        localStorage.setItem('bobo-food-plan-theme', newTheme);
        if (auth.currentUser) {
          saveUserThemePreference(newTheme);
        }
      }
    },

    // Utilitaires
    getThemeClass: () => theme,
    applyThemeToDocument,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};
